/**
 * Onboarding frame (V1 auth-profiles-gdpr) — auth gate + step rail +
 * the "Delete my data" footer that S-06…S-09 all carry (screens.md S-05).
 * Lifecycle-aware: an already-ACTIVE account has no business here → /deck.
 *
 * v3 "AFTER-DARK ARCADE": onboarding lives OUTSIDE the app shell, so the
 * frame mounts its own atmosphere (ambient neon + film grain) — signing up
 * is walking into the arcade after dark. The step rail is a lit wire; the
 * current station glows accent, finished ones stay warm. Each step content
 * block rises in with the shared page spring. Same DOM contract as v1
 * (.onboard, data-screen, aria-current, step classes).
 */
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AmbientBackground,
  GrainOverlay,
  IconBrokenHeartFill,
  pageVariants,
  Skeleton,
} from "../../design/index.js";
import { sessionStore, useStore } from "../../lib/store.js";
import { DeleteDataFooter } from "../account/delete-sheet.js";
import "./onboarding.css";

const STEPS = [
  { path: "/onboard/identity", label: "Identity" },
  { path: "/onboard/profile", label: "Profile" },
  { path: "/onboard/location", label: "Location" },
  { path: "/onboard/briefing", label: "Briefing" },
] as const;

export interface OnboardingFrameProps {
  step: 1 | 2 | 3 | 4;
  screen: string;
  children: ReactNode;
}

export function OnboardingFrame({ step, screen, children }: OnboardingFrameProps) {
  const status = useStore(sessionStore, (s) => s.status);
  const lifecycle = useStore(sessionStore, (s) => s.lifecycle);

  if (status === "unknown") {
    return (
      <div className="onboard">
        <Skeleton height={40} radius={10} style={{ marginBottom: 12 }} />
        <Skeleton height={280} radius={18} />
      </div>
    );
  }
  if (status === "anon") return <Navigate to="/" replace />;
  if (lifecycle === "deletion_grace") return <Navigate to="/grace" replace />;
  if (lifecycle === "banned") return <Navigate to="/banned" replace />;

  return (
    <div className="onboard" data-screen={screen}>
      <AmbientBackground />
      <GrainOverlay />
      <header className="onboard__head">
        <span className="onboard__brand">
          <IconBrokenHeartFill width={20} height={20} style={{ color: "var(--accent)" }} />
          humble
        </span>
        <ol className="onboard__steps" aria-label="Onboarding progress">
          {STEPS.map((s, i) => (
            <li
              key={s.path}
              className={`onboard__step${i + 1 === step ? " onboard__step--now" : ""}${i + 1 < step ? " onboard__step--done" : ""}`}
              aria-current={i + 1 === step ? "step" : undefined}
            >
              {s.label}
            </li>
          ))}
        </ol>
      </header>
      <motion.main
        className="onboard__main"
        key={step}
        variants={pageVariants}
        initial="initial"
        animate="enter"
      >
        {children}
      </motion.main>
      <footer className="onboard__foot">
        <DeleteDataFooter />
      </footer>
    </div>
  );
}
